import {
  BLISS_BASIC_MATTRESS_SIZES,
  blissBasicLayoutVizForChoice,
  mattressFootprintLabel,
  type BlissBasicMattressSizeId,
  type BlissBasicLayoutVizSpec,
} from '../../config/blissBasicMattressSizes'

const PX_PER_IN = 2.15

function MattressRect({
  sizeId,
  className = '',
}: {
  sizeId: BlissBasicMattressSizeId
  className?: string
}) {
  const m = BLISS_BASIC_MATTRESS_SIZES[sizeId]
  return (
    <div
      className={`bb-mattress-rect ${className}`.trim()}
      style={{
        width: `${Math.round(m.widthIn * PX_PER_IN)}px`,
        height: `${Math.round(m.lengthIn * PX_PER_IN)}px`,
      }}
      title={mattressFootprintLabel(sizeId)}
    >
      <span className="bb-mattress-rect-label">{m.label}</span>
      <span className="bb-mattress-rect-dim">
        {m.widthIn}×{m.lengthIn}&quot;
      </span>
    </div>
  )
}

function StackViz({ layers }: { layers: BlissBasicMattressSizeId[] }) {
  return (
    <div className="bb-mattress-stack" aria-hidden>
      {[...layers].reverse().map((id, i) => (
        <MattressRect key={`${id}-${i}`} sizeId={id} />
      ))}
    </div>
  )
}

function CornerViz({ bed }: { bed: BlissBasicMattressSizeId }) {
  return (
    <div className="bb-mattress-corner" aria-hidden>
      <div className="bb-mattress-corner-row">
        <MattressRect sizeId={bed} />
        <MattressRect sizeId={bed} />
      </div>
      <div className="bb-mattress-corner-row">
        <MattressRect sizeId={bed} />
        <span className="bb-mattress-corner-spacer" />
      </div>
    </div>
  )
}

function LayoutDiagram({ spec }: { spec: BlissBasicLayoutVizSpec }) {
  if (spec.type === 'stack') return <StackViz layers={spec.layers} />
  if (spec.type === 'corner') return <CornerViz bed={spec.bed} />
  return (
    <p className="bb-mattress-unsure">We&apos;ll match mattress sizes to your room after you submit.</p>
  )
}

type Props = {
  choiceKey: string
  compact?: boolean
}

export default function BlissBasicMattressLayoutViz({ choiceKey, compact = false }: Props) {
  const spec = blissBasicLayoutVizForChoice(choiceKey)
  return (
    <div className={`bb-mattress-viz${compact ? ' bb-mattress-viz--compact' : ''}`}>
      <LayoutDiagram spec={spec} />
    </div>
  )
}

/** Side-by-side reference for twin / full / queen. */
export function BlissBasicMattressSizeLegend() {
  const ids: BlissBasicMattressSizeId[] = ['twin', 'full', 'queen']
  return (
    <div className="bb-mattress-legend" aria-label="Standard mattress size comparison">
      <p className="bb-mattress-legend-title">Compare standard mattress footprints</p>
      <div className="bb-mattress-legend-row">
        {ids.map((id) => (
          <div key={id} className="bb-mattress-legend-item">
            <MattressRect sizeId={id} className="bb-mattress-rect--legend" />
          </div>
        ))}
      </div>
    </div>
  )
}
